<template>
    <MainContent title="Detalle del deportista">
        <template #header>
            <DatosDeportistaCard
                v-if="base"
                :base="base"
                :manager="manager"
            />
        </template>

        <div v-if="loading" class="tw:space-y-6">
            <!-- Skeleton: DatosDeportistaCard -->
            <div class="tw:bg-white tw:rounded-xl tw:shadow-sm tw:border tw:border-gray-100 tw:overflow-hidden tw:space-y-6">
                <div class="tw:bg-gray-800 tw:opacity-30 tw:px-6 tw:py-4">
                    <SkeletonItem height="tw:h-4" customClass="tw:w-40 tw:bg-white/40" />
                </div>
                <div class="tw:p-6 tw:space-y-5">
                    <!-- Section label: Datos del menor -->
                    <div class="tw:flex tw:items-center tw:gap-1.5">
                        <SkeletonItem height="tw:h-3" customClass="tw:w-3 tw:rounded-full" />
                        <SkeletonItem height="tw:h-3" customClass="tw:w-28" />
                    </div>
                    <!-- 14 fields in 3-column grid -->
                    <div class="tw:grid tw:grid-cols-1 tw:md:grid-cols-3 tw:gap-4">
                        <div v-for="n in 14" :key="'field-' + n" class="tw:space-y-1.5">
                            <SkeletonItem height="tw:h-2.5" customClass="tw:w-24" />
                            <SkeletonItem height="tw:h-4" customClass="tw:w-5/6 tw:bg-gray-100" />
                        </div>
                    </div>
                    <!-- Separator -->
                    <div class="tw:border-t tw:border-gray-100 tw:pt-1"></div>
                    <!-- Section label: Datos del equipo -->
                    <div class="tw:flex tw:items-center tw:gap-1.5">
                        <SkeletonItem height="tw:h-3" customClass="tw:w-3 tw:rounded-full" />
                        <SkeletonItem height="tw:h-3" customClass="tw:w-24" />
                    </div>
                    <!-- 3 fields in 3-column grid -->
                    <div class="tw:grid tw:grid-cols-1 tw:md:grid-cols-3 tw:gap-4">
                        <div v-for="n in 3" :key="'team-field-' + n" class="tw:space-y-1.5">
                            <SkeletonItem height="tw:h-2.5" customClass="tw:w-20" />
                            <SkeletonItem height="tw:h-4" customClass="tw:w-3/4 tw:bg-gray-100" />
                        </div>
                    </div>
                </div>
            </div>

            <!-- Skeleton: TabsPanel — refleja la estructura de DatosPersonalesCard -->
            <div class="tw:bg-white tw:rounded-xl tw:shadow-sm tw:border tw:border-gray-100 tw:overflow-hidden">
                <div class="tw:border-b tw:border-gray-200 tw:px-4 tw:flex tw:gap-0">
                    <SkeletonItem
                        v-for="n in 6" :key="'tab-' + n"
                        height="tw:h-8" customClass="tw:w-28 tw:rounded-t tw:bg-gray-100 tw:mt-2 tw:mr-3"
                    />
                </div>
                <div class="tw:p-6 tw:space-y-6">
                    <!-- Title -->
                    <div class="tw:space-y-1">
                        <SkeletonItem height="tw:h-5" customClass="tw:w-36" />
                        <SkeletonItem height="tw:h-3" customClass="tw:w-56 tw:bg-gray-100" />
                    </div>

                    <!-- Bloque 1: Responsables + Contacto -->
                    <div class="tw:space-y-3">
                        <!-- Responsable 1 -->
                        <div class="tw:flex tw:items-center tw:gap-1.5">
                            <SkeletonItem height="tw:h-3" customClass="tw:w-3 tw:rounded-full" />
                            <SkeletonItem height="tw:h-3" customClass="tw:w-28" />
                        </div>
                        <div class="tw:grid tw:grid-cols-1 tw:md:grid-cols-3 tw:gap-3">
                            <div v-for="n in 5" :key="'resp1-' + n" class="tw:space-y-1.5">
                                <SkeletonItem height="tw:h-2.5" customClass="tw:w-16" />
                                <SkeletonItem height="tw:h-4" customClass="tw:w-5/6 tw:bg-gray-100" />
                            </div>
                        </div>

                        <!-- Separador -->
                        <div class="tw:h-[0.5px] tw:bg-gray-100 tw:w-full"></div>

                        <!-- Contacto y dirección -->
                        <div class="tw:flex tw:items-center tw:gap-1.5">
                            <SkeletonItem height="tw:h-3" customClass="tw:w-3 tw:rounded-full" />
                            <SkeletonItem height="tw:h-3" customClass="tw:w-36" />
                        </div>
                        <div class="tw:grid tw:grid-cols-1 tw:md:grid-cols-3 tw:gap-4">
                            <div v-for="n in 3" :key="'contact-' + n" class="tw:space-y-1.5">
                                <SkeletonItem height="tw:h-2.5" customClass="tw:w-20" />
                                <SkeletonItem height="tw:h-4" customClass="tw:w-4/5 tw:bg-gray-100" />
                            </div>
                        </div>
                    </div>

                    <div class="tw:border-t tw:border-gray-100"></div>

                    <!-- Bloque 2: Documentación -->
                    <div class="tw:space-y-3">
                        <div class="tw:flex tw:items-center tw:gap-1.5">
                            <SkeletonItem height="tw:h-3" customClass="tw:w-3 tw:rounded-full" />
                            <SkeletonItem height="tw:h-3" customClass="tw:w-28" />
                        </div>
                        <!-- Dropzone skeleton -->
                        <div class="tw:border-2 tw:border-dashed tw:border-gray-200 tw:rounded-lg tw:p-8 tw:flex tw:flex-col tw:items-center tw:gap-2">
                            <SkeletonItem height="tw:h-7" customClass="tw:w-7" />
                            <SkeletonItem height="tw:h-3" customClass="tw:w-48" />
                            <SkeletonItem height="tw:h-2.5" customClass="tw:w-36 tw:bg-gray-100" />
                        </div>
                    </div>

                    <div class="tw:border-t tw:border-gray-100"></div>

                    <!-- Bloque 3: Observaciones -->
                    <div class="tw:space-y-3">
                        <div class="tw:flex tw:items-center tw:gap-1.5">
                            <SkeletonItem height="tw:h-3" customClass="tw:w-3 tw:rounded-full" />
                            <SkeletonItem height="tw:h-3" customClass="tw:w-24" />
                        </div>
                        <!-- Textarea skeleton -->
                        <SkeletonItem height="tw:h-8" customClass="tw:w-full tw:bg-gray-100 tw:rounded-lg" />
                        <!-- Botón skeleton -->
                        <div class="tw:flex tw:justify-end">
                            <SkeletonItem height="tw:h-9" customClass="tw:w-36 tw:rounded-lg" />
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div v-else-if="base" class="tw:grid tw:grid-cols-1 tw:gap-6" id="deportista-tabs">
            <TabsPanel
                :tabs="tabs"
                :initial-tab="$route.query.tab"
                @tab-change="onTabChange"
            >
                <template #tab-datos-personales>
                    <DatosPersonalesCard
                        :base="base"
                        :url="url_documentacion"
                        :lista-id="lista_id"
                        :base-id="base_id"
                        @refresh="refreshBaseAndScroll"
                        @editar-observacion="editarObservacion"
                    />
                </template>
                <template #tab-datos-deportivos>
                    <DatosDeportivosCard
                        :base="base"
                        @refresh="refreshBaseAndScroll"
                    />
                </template>
                <template #tab-lesiones>
                    <LesionesCard
                        :lesiones="base.lesiones || []"
                        :fetched="fetchedTabs.lesiones"
                        :loading="lesionesLoading"
                        :lesiones-search="lesionesSearch"
                        :lesiones-filter="lesionesFilter"
                        @add-lesion="openAddLesion"
                        @delete-lesion="openBorrarLesion"
                        @update:lesionesSearch="updateLesionesSearch"
                        @update:lesionesFilter="updateLesionesFilter"
                    />
                </template>
                <template #tab-sanciones>
                    <SancionesCard
                        :sanciones="base.sanciones || []"
                        :base="base"
                        :fetched="fetchedTabs.sanciones"
                        :loading="sancionesLoading"
                        @add-sancion="openAddSancion"
                        @borrar-sancion="onBorrarSancion"
                        @update:sancionesSearch="updateSancionesSearch"
                        @update:sancionesFilter="updateSancionesFilter"
                    />
                </template>
                <template #tab-transacciones>
                    <TransaccionesCard
                        :transacciones="base.transacciones"
                        :base="base"
                        @editar-transaccion="onEditarTransaccion"
                    />
                </template>
                <template #tab-indumentarias>
                    <IndumentariasCard
                        :indumentarias="base.indumentarias"
                        @editar-indumentaria="openEditarIndumentaria"
                    />
                </template>
                <template #tab-plazos>
                    <PlazosCard
                        :plazos="base.plazos"
                        :base="base"
                        @pagar-plazo="(plazo_id, metodo) => pagarPlazo(plazo_id, metodo)"
                        @pagar-plazos="(metodo) => pagarPlazos(metodo)"
                        @descontar-plazo="(integro, porcentaje, id) => descontarPlazo(integro, porcentaje, id)"
                        @generar-enlace-pago="openGenerarEnlacePago"
                    />
                </template>
            </TabsPanel>

            <!--
                Modales:
                - Los 3 modales de plazo (PagarPlazoModal, PagarTodosPlazosModal, DescuentoPlazoModal)
                  son montados y gestionados internamente por PlazosCard (fork del patrón abonado).
                - Show.vue solo monta los 7 modales restantes que las cards emiten hacia arriba.
            -->
            <AddSancion
                :deportista="base"
                :on-submit="addSancion"
            />
            <BorrarSancion
                v-if="selectedSancion"
                :deportista="base"
                :sancion="selectedSancion"
                :on-confirm="borrarSancion"
                @close="selectedSancion = null"
            />
            <AddLesion
                :deportista="base"
                :on-submit="addLesion"
            />
            <BorrarLesion
                v-if="selectedLesion"
                :deportista="base"
                :lesion="selectedLesion"
                :on-confirm="deleteLesion"
                @close="selectedLesion = null"
            />
            <EditarIndumentaria
                v-if="selectedIndumentaria"
                :indumentaria="selectedIndumentaria"
                :lista-id="lista_id"
                :base-id="base_id"
                @actualizar="refreshBaseAndScroll"
                @close="selectedIndumentaria = null"
            />
            <GenerarEnlacePago
                v-if="selectedPlazo"
                :plazo="selectedPlazo"
                :on-submit="generarEnlacePago"
                @close="selectedPlazo = null"
            />
            <EditarTransaccionModal
                v-if="selectedTransaccion"
                :transaccion="selectedTransaccion"
                :params="{ filters: {}, lista_id: lista_id, base_id: base_id }"
                vuexAlias="back.bases.transacciones.editar"
                @cerrar="onEditarTransaccionClose"
            />
        </div>
    </MainContent>
</template>

<script setup>
/*
 * REDISEÑO DETALLE DEPORTISTAS — PATRÓN REPLICADO 1:1 DE ABONADOS
 * ---------------------------------------------------------------
 * Módulo deportistas/ creado desde cero siguiendo el patrón Composition API + Tailwind
 * de src/modules/admin/abonados/views/Show.vue. Reemplaza el legacy bootstrap-vue
 * de src/views/backend/bases/Show.vue.
 *
 * Estructura: Show.vue orquesta MainContent + TabsPanel + 8 Cards + 10 Modales,
 * delegando toda la lógica de negocio al composable useDeportistaShow.
 *
 * Tasks completadas: 47/47 (ver sdd/redisenyo-detalle-deportistas/tasks en engram).
 *
 * Issues conocidos (fuera de scope):
 *  - ensureIndumentariasLoaded sin retry automático si la carga inicial falla.
 */
    import { onMounted, ref, nextTick, getCurrentInstance, computed } from 'vue'
    import store from '@/store'
    import { config } from '@/config.js'
    import { useToast } from '@/composables/common/useToast'

    import MainContent from '@/modules/admin/ui/MainContent.vue'
    import TabsPanel from '@/modules/admin/ui/TabsPanel.vue'
    import { SkeletonItem } from '@/modules/admin/ui/skeleton'

    // Cards
    import DatosDeportistaCard from '@/modules/admin/bases/components/deportista/DatosDeportistaCard.vue'
    import DatosPersonalesCard from '@/modules/admin/bases/components/deportista/DatosPersonalesCard.vue'
    import DatosDeportivosCard from '@/modules/admin/bases/components/deportista/DatosDeportivosCard.vue'
    import LesionesCard from '@/modules/admin/bases/components/deportista/LesionesCard.vue'
    import SancionesCard from '@/modules/admin/bases/components/deportista/SancionesCard.vue'
    import TransaccionesCard from '@/modules/admin/bases/components/deportista/TransaccionesCard.vue'
    import IndumentariasCard from '@/modules/admin/bases/components/deportista/IndumentariasCard.vue'
    import PlazosCard from '@/modules/admin/bases/components/deportista/PlazosCard.vue'

    // Modales
    import AddSancion from '@/modules/admin/bases/components/modals/AddSancion.vue'
    import BorrarSancion from '@/modules/admin/bases/components/modals/BorrarSancion.vue'
    import AddLesion from '@/modules/admin/bases/components/modals/AddLesion.vue'
    import BorrarLesion from '@/modules/admin/bases/components/modals/BorrarLesion.vue'
    import EditarIndumentaria from '@/modules/admin/bases/components/modals/EditarIndumentaria.vue'
    import GenerarEnlacePago from '@/modules/admin/bases/components/modals/GenerarEnlacePago.vue'
    import EditarTransaccionModal from '@/modules/admin/transacciones/modals/EditarTransaccionModal.vue'

    import { useDeportistaShow } from '@/composables/admin/bases/useDeportistaShow'

    const props = defineProps({
        lista_id: {
            type: [String, Number],
            required: true
        },
        base_id: {
            type: [String, Number],
            required: true
        }
    })

    const toast = useToast()
    const instance = getCurrentInstance()

    // ==================== COMPOSABLE ====================

    const {
        base,
        loading,
        fetchedTabs,
        tabs,
        url_documentacion,
        manager,
        teatro,
        fetchAll,
        getBase,
        getLesiones,
        getSanciones,
        lesionesSearch,
        lesionesFilter,
        updateLesionesSearch,
        updateLesionesFilter,
        updateSancionesSearch,
        updateSancionesFilter,
        pagarPlazo,
        pagarPlazos,
        descontarPlazo,
        generarEnlacePago,
        addSancion,
        borrarSancion,
        addLesion,
        deleteLesion,
    } = useDeportistaShow(props)

    // ==================== COMPUTED ====================

    const titulo = computed(() => {
        if (!base.value) return 'Cargando...'
        const name = base.value.nombre || base.value.name || ''
        const surnames = base.value.apellidos || base.value.surnames || ''
        return [name, surnames].filter(Boolean).join(' ')
    })

    // ==================== REFS ====================

    const selectedSancion = ref(null)
    const selectedLesion = ref(null)
    const selectedIndumentaria = ref(null)
    const selectedPlazo = ref(null)
    const selectedTransaccion = ref(null)
    const lesionesLoading = ref(false)
    const sancionesLoading = ref(false)

    // ==================== REFRESH WITH SCROLL ====================

    async function refreshBaseAndScroll() {
        await getBase()

        if (instance.proxy.$route.query.tab) {
            await nextTick()
            instance.proxy.$scrollTo('#deportista-tabs', 500, { offset: -50 })
        }
    }

    // ==================== TAB CHANGE ====================

    async function onTabChange(tabKey) {
        fetchedTabs.value = { ...fetchedTabs.value, [tabKey]: true }

        // Solo fetch si no hay datos cacheados aún.
        // Si el usuario vuelve al tab, persisten los datos anteriores.
        if (tabKey === 'lesiones' && !base.value?.lesiones) {
            lesionesLoading.value = true
            try { await getLesiones() } finally { lesionesLoading.value = false }
        }
        if (tabKey === 'sanciones' && !base.value?.sanciones) {
            sancionesLoading.value = true
            try { await getSanciones() } finally { sancionesLoading.value = false }
        }
    }

    // ==================== MODAL OPEN HANDLERS ====================

    function openAddSancion() {
        instance.proxy.$modal.show('addSancion')
    }

    async function onBorrarSancion({ sancionId }) {
        if (!base.value || !base.value.sanciones) return
        selectedSancion.value = base.value.sanciones.find(s => s.id === sancionId)
        await nextTick()
        instance.proxy.$modal.show('borrar-sancion-' + sancionId)
    }

    function openAddLesion() {
        instance.proxy.$modal.show('addLesion')
    }

    async function openBorrarLesion(lesion) {
        selectedLesion.value = lesion
        await nextTick()
        instance.proxy.$modal.show('borrar-lesion-' + lesion.id)
    }

    async function openEditarIndumentaria(indumentaria) {
        selectedIndumentaria.value = indumentaria
        await nextTick()
        instance.proxy.$modal.show('editar-' + indumentaria.id)
    }

    async function openGenerarEnlacePago(plazoId) {
        if (!base.value || !base.value.plazos) return
        selectedPlazo.value = base.value.plazos.find(p => p.id === plazoId)
        await nextTick()
        instance.proxy.$modal.show('generar-enlace-pago-' + plazoId)
    }

    async function onEditarTransaccion(transaccion) {
        selectedTransaccion.value = transaccion
        await nextTick()
        instance.proxy.$modal.show('editTransaccion-' + transaccion.id)
    }

    function onEditarTransaccionClose(modalName, actualizar) {
        if (modalName) {
            instance.proxy.$modal.hide(modalName)
        }
        selectedTransaccion.value = null
        if (actualizar) {
            refreshBaseAndScroll()
        }
    }

    // ==================== OBSERVACIÓN HANDLER ====================

    async function editarObservacion(observacionText) {
        try {
            await store.dispatch(
                config.getVuexAlias('back.bases.update'),
                {
                    base_id: props.base_id,
                    lista_id: props.lista_id,
                    observaciones_club: observacionText
                }
            )
            toast.success('Observación actualizada')
        } catch (error) {
            console.error('Error updating observacion:', error)
            toast.error('Error al actualizar la observación')
        }
    }

    // ==================== LIFECYCLE ====================

    onMounted(async() => {
        await fetchAll()

        const initialTab = instance.proxy.$route.query.tab
        if (initialTab) {
            fetchedTabs.value = { ...fetchedTabs.value, [initialTab]: true }
            await nextTick()
            instance.proxy.$scrollTo('#deportista-tabs', 500, { offset: -50 })
        }
    })
</script>
